import React, { useEffect, useMemo, useState } from "react";
import {
  Box, Typography, Stack, Button, Grid, Paper, alpha, useTheme,
  Container, IconButton, Divider, CircularProgress, Chip,
  Dialog, DialogTitle, DialogContent, SwipeableDrawer, useMediaQuery,
  List, ListItem, ListItemButton, ListItemText, Radio, ListItemIcon
} from "@mui/material";
import {
  Person as PersonIcon, Groups as GroupsIcon,
  ArrowBack as BackIcon, InfoOutlined, CalendarMonth,
  CheckCircle as SuccessIcon, Stars
} from "@mui/icons-material";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getOneRoom, getRoomsItems } from "../redux/slices/room/thunk";
import { createBooking, getAllBookingTimes } from "../redux/slices/booking/thunk";
import LoadingPage from "../components/LoadingComponent";
import { useTranslation } from "react-i18next";

const BASE_URL = "https://red-90.com";
const joinUrl = (path) => path?.startsWith("http") ? path : `${BASE_URL}${path}`;

const formatAmPm = (date, lang) => {
  return date.toLocaleTimeString(lang === "ar" ? "ar-EG" : "en-US", {
    hour: "numeric", minute: "2-digit", hour12: true,
  });
};

function buildSmartSlots() {
  const slots = [];
  const now = new Date();
  const startHour = new Date(now);
  startHour.setHours(startHour.getHours() + 1, 0, 0, 0);

  // Set the limit to the end of tomorrow (11:00 PM tomorrow)
  const tomorrowLimit = new Date(now);
  tomorrowLimit.setDate(tomorrowLimit.getDate() + 1);
  tomorrowLimit.setHours(23, 59, 59, 999);

  let cursor = new Date(startHour);
  while (cursor <= tomorrowLimit) {
    const hour = cursor.getHours();
    
    // Skip slots starting from 6 AM up to 9 AM (i.e. 6 AM to 10 AM window)
    if (hour >= 6 && hour < 10) {
      cursor.setHours(cursor.getHours() + 1);
      continue;
    }

    const dayKey = cursor.getDate() === now.getDate() ? "today" : "tomorrow";
    slots.push({ key: cursor.toISOString(), start: new Date(cursor), dayKey });
    cursor.setHours(cursor.getHours() + 1);
  }
  return slots;
}

export default function BookRoomPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();
  const { i18n, t } = useTranslation();
  const isRtl = i18n.language === "ar";
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const { room, roomsItems } = useSelector((state) => state.room);
  const { bookingTimes } = useSelector((state) => state.booking);

  // States
  const [initialLoading, setInitialLoading] = useState(true);
  const [bookingKind, setBookingKind] = useState("single");
  const [startSlotKey, setStartSlotKey] = useState(null);
  const [endSlotKey, setEndSlotKey] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [openItemsMenu, setOpenItemsMenu] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  // جلب البيانات كلها مرة واحدة قبل فتح الصفحة
  useEffect(() => {
    navigate('/home')
    const fetchData = async () => {
      try {
        setInitialLoading(true);
        await Promise.all([
          dispatch(getOneRoom(id)).unwrap(),
          dispatch(getRoomsItems()).unwrap(),
          dispatch(getAllBookingTimes({ query: `roomId=${id}` })).unwrap()
        ]);

        // فتح المودال تلقائياً بعد تحميل البيانات
        setTimeout(() => setOpenItemsMenu(true), 600);
      } catch (error) {
        console.error("Failed to fetch data:", error);
      } finally {
        setInitialLoading(false);
      }
    };

    fetchData();
  }, [dispatch, id]);

  const allSlots = useMemo(() => buildSmartSlots(), []);



  const isSlotBooked = (slotTime) => {
    if (!bookingTimes?.data) return false;
    const time = new Date(slotTime).getTime();
    return bookingTimes.data.some((res) => {
      const resStart = new Date(res.reservedStart).getTime();
      const resEnd = new Date(res.reservedEnd).getTime();
      return time >= resStart && time < resEnd;
    });
  };

  const handleSlotClick = (slotKey) => {
    // إذا لم يكن هناك بداية، أو كان هناك بداية ونهاية بالفعل (يعني المستخدم بدأ تحديد جديد)
    if (!startSlotKey || (startSlotKey && endSlotKey)) {
      setStartSlotKey(slotKey);
      setEndSlotKey(null);
      return;
    }

    // إذا كان هناك بداية ونحن نختار النهاية الآن
    if (startSlotKey && !endSlotKey) {
      const sIdx = allSlots.findIndex(x => x.key === startSlotKey);
      const currIdx = allSlots.findIndex(x => x.key === slotKey);

      // 1. إذا اختار المستخدم وقت قبل وقت البداية، اجعل الاختيار الجديد هو البداية
      if (currIdx < sIdx) {
        setStartSlotKey(slotKey);
        return;
      }

      // 2. التحقق من وجود أي Slot محجوز أو فجوة في المسافة بين البداية والنهاية المختارة
      const rangeToCheck = allSlots.slice(sIdx, currIdx + 1);
      const hasReservedSlot = rangeToCheck.some(slot => isSlotBooked(slot.start));
      
      let hasGap = false;
      for (let i = 0; i < rangeToCheck.length - 1; i++) {
        const diffMs = new Date(rangeToCheck[i + 1].start).getTime() - new Date(rangeToCheck[i].start).getTime();
        if (diffMs > 3600000) { // check if difference is more than 1 hour (3,600,000 ms)
          hasGap = true;
          break;
        }
      }

      if (hasReservedSlot || hasGap) {
        // إذا وجدنا Slot محجوز أو فجوة في الطريق، نعتبر الاختيار الجديد هو "بداية جديدة"
        setStartSlotKey(slotKey);
        setEndSlotKey(null);
      } else {
        // إذا كان الطريق خالياً، نعتمد النهاية بشكل طبيعي
        setEndSlotKey(slotKey);
      }
    }
  };

  const selectedRange = useMemo(() => {
    if (!startSlotKey) return [];
    const sIdx = allSlots.findIndex((x) => x.key === startSlotKey);
    if (!endSlotKey) return [allSlots[sIdx]];
    const eIdx = allSlots.findIndex((x) => x.key === endSlotKey);
    return allSlots.slice(sIdx, eIdx + 1);
  }, [startSlotKey, endSlotKey, allSlots]);

  const hasBookedSlotInRange = useMemo(() => {
    if (selectedRange.length < 2) return false;
    // Check if any slot in the user's selection is already taken
    return selectedRange.some(slot => isSlotBooked(slot.start));
  }, [selectedRange, bookingTimes]);

  const totalHours = selectedRange.length > 1 ? selectedRange.length - 1 : 0;
  const currentPrice = bookingKind === "single" ? room?.price_single_hour : room?.price_multi_hour;
  const totalPrice = totalHours * currentPrice;

  // القائمة الرأسية مع Radio Button
  const RenderOptions = () => (
    <Box sx={{ pb: isMobile ? 4 : 2 }}>
      <Typography variant="body2" color="text.secondary" fontWeight={600} sx={{ px: 2, mb: 1 }}>
        {isRtl ? "كيف نجهز لك الغرفة؟" : "How should we prepare the room?"}
      </Typography>
      <List>
        {roomsItems?.map((item) => {
          const isSelected = selectedItem === item._id;
          return (
            <ListItem key={item._id} disablePadding>
              <ListItemButton
                onClick={() => {
                  setSelectedItem(isSelected ? null : item._id);
                  setTimeout(() => setOpenItemsMenu(false), 300);
                }}
                sx={{
                  py: 1.5,
                  px: 2,
                  display: 'flex',
                  flexDirection: 'row', // يضمن الترتيب الأفقي
                  alignItems: 'center',
                  gap: 1, // مسافة ثابتة وصغيرة بين الـ Radio والنص
                  bgcolor: isSelected ? alpha(theme.palette.primary.main, 0.05) : 'transparent',
                  textAlign: isRtl ? 'right' : 'left', // محاذاة النص حسب اللغة
                }}
              >
                {/* نستخدم Radio مباشرة بدون ListItemIcon لنتحكم في المسافة أفضل */}
                <Radio
                  checked={isSelected}
                  color="primary"
                  sx={{ p: 0 }} // إلغاء الحواف الداخلية للـ Radio ليلتصق بالنص
                />

                <ListItemText
                  primary={isRtl ? item.title_ar : item.title_en}
                  primaryTypographyProps={{
                    fontWeight: 700,
                    sx: {
                      m: 0,
                      textAlign: isRtl ? 'right' : 'left'
                    }
                  }}
                  sx={{ m: 0 }} // إلغاء أي هوامش افتراضية
                />
              </ListItemButton>
            </ListItem>
          );
        })}
        <ListItem disablePadding>
          <ListItemButton onClick={() => setOpenItemsMenu(false)} sx={{ textAlign: 'center', py: 2 }}>
            <ListItemText
              primary={isRtl ? "تخطي / لا شيء" : "Skip / None"}
              primaryTypographyProps={{ fontWeight: 600, color: 'text.secondary' }}
            />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  if (initialLoading || !room) return <LoadingPage />;

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default", pb: 10 }}>
      {/* Header */}
      <Box sx={{ bgcolor: theme.palette.primary.softBackground, borderBottom: `1px solid ${theme.palette.divider}`, mb: 4 }}>
        <Container maxWidth="lg">
          <Stack direction="row" alignItems="center" spacing={2} sx={{ py: 4 }}>
            <IconButton onClick={() => navigate(-1)} sx={{ bgcolor: "background.paper", border: `1px solid ${theme.palette.divider}` }}>
              <BackIcon sx={{ transform: isRtl ? "rotate(180deg)" : "none", fontSize: 18 }} />
            </IconButton>
            <Box>
              <Typography variant="h4" fontWeight={900}>{t("bookRoom.page_title")}</Typography>
              <Typography variant="body2" color="text.secondary">{isRtl ? "احجز مكانك المفضل" : "Book your favorite spot"}</Typography>
            </Box>
          </Stack>
        </Container>
      </Box>

      <Container maxWidth="lg">
        <Grid container spacing={4}>
          <Grid item xs={12} md={8}>
            <Stack spacing={4}>

              {/* Service Trigger Card */}
              <Paper
                elevation={0} onClick={() => setOpenItemsMenu(true)}
                sx={{
                  p: 1.5, borderRadius: 2, cursor: 'pointer', border: '1px dashed',
                  borderColor: 'primary.main', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  bgcolor: alpha(theme.palette.primary.main, 0.02)
                }}
              >
                <Stack direction="row" spacing={2} gap={1} alignItems="center">
                  <Stars color="primary" />
                  <Box>
                    <Typography fontWeight={800}>{isRtl ? "ماذا ستفعل اليوم؟" : "What's the plan?"}</Typography>
                    <Typography variant="caption" color="primary.main" fontWeight={700}>
                      {selectedItem
                        ? (isRtl ? roomsItems.find(i => i._id === selectedItem)?.title_ar : roomsItems.find(i => i._id === selectedItem)?.title_en)
                        : (isRtl ? "اضغط لاختيار خدمة إضافية" : "Tap to add a service")}
                    </Typography>
                  </Box>
                </Stack>
                <Button size="small">{isRtl ? "تغيير" : "Change"}</Button>
              </Paper>

              {/* Kind Selection */}
              <Box>
                <Typography variant="h6" fontWeight={800} mb={2}>{t("bookRoom.select_kind")}</Typography>
                <Grid container spacing={2}>
                  {["single", "multi"].map((k) => (
                    <Grid item xs={6} key={k}>
                      <Paper
                        elevation={0} onClick={() => setBookingKind(k)}
                        sx={{
                          p: 2, cursor: 'pointer', borderRadius: 2, border: '2px solid', transition: '0.3s',
                          borderColor: bookingKind === k ? 'primary.main' : 'divider',
                          bgcolor: bookingKind === k ? alpha(theme.palette.primary.main, 0.04) : 'background.paper',
                          textAlign: 'center'
                        }}
                      >
                        <Box sx={{ mb: 1, display: 'inline-flex', p: 1, borderRadius: '50%', bgcolor: theme.palette.primary.softBackground }}>
                          {k === "single" ? <PersonIcon color="primary" /> : <GroupsIcon color="primary" />}
                        </Box>
                        <Typography fontWeight={700} display="block">{t(`bookRoom.kind.${k}`)}</Typography>
                        <Typography variant="subtitle1" fontWeight={900} color="primary">
                          {k === "single" ? room.price_single_hour : room.price_multi_hour} EGP
                        </Typography>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </Box>

              {/* Slots Section */}
              <Box>
                <Typography variant="h6" fontWeight={800} mb={3}>{t("bookRoom.choose_time")}</Typography>
                {["today", "tomorrow"].map((day) => {
                  const daySlots = allSlots.filter(s => s.dayKey === day);
                  if (daySlots.length === 0) return null;
                  return (
                    <Box key={day} sx={{ mb: 4 }}>
                      <Typography variant="subtitle2" fontWeight={700} color="text.secondary" mb={2} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CalendarMonth fontSize="small" /> {t(`bookRoom.${day}`)}
                      </Typography>
                      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: 1.5 }}>
                        {daySlots.map((slot) => {
                          const isBooked = isSlotBooked(slot.start);
                          const isSelected = selectedRange.some(r => r.key === slot.key);
                          return (
                            <Button
                              key={slot.key} disabled={isBooked}
                              onClick={() => handleSlotClick(slot.key)}
                              variant={isSelected ? "contained" : "outlined"}
                              sx={{ py: 1.5, borderRadius: 3, fontWeight: 700 }}
                            >
                              {formatAmPm(slot.start, i18n.language)}
                            </Button>
                          );
                        })}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Stack>
          </Grid>

          {/* Sidebar Summary */}
          <Grid item xs={12} md={4}>
            <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: `1px solid ${theme.palette.divider}`, position: { md: 'sticky' }, top: 24 }}>
              <Typography variant="h6" fontWeight={900} mb={3}>{t("bookRoom.summary")}</Typography>
              <Stack direction="row" spacing={2} mb={3}>
                <Box component="img" src={joinUrl(room.image)} sx={{ width: 60, height: 60, borderRadius: 1, objectFit: 'cover' }} />
                <Box>
                  <Typography fontWeight={800} variant="body2">{isRtl ? room.title_ar : room.title_en}</Typography>
                  <Chip size="small" label={room.type} sx={{ mt: 0.5, fontWeight: 700 }} />
                </Box>
              </Stack>
              <Divider sx={{ my: 2, borderStyle: 'dashed' }} />
              <Stack spacing={2}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography color="text.secondary" variant="body2">{t("bookRoom.duration")}</Typography>
                  <Typography fontWeight={800}>{totalHours} {t("bookRoom.hours")}</Typography>
                </Stack>
                {selectedItem && (
                  <Stack direction="row" justifyContent="space-between">
                    <Typography color="text.secondary" variant="body2">{isRtl ? "الخدمة" : "Service"}</Typography>
                    <Typography fontWeight={800} color="primary.main">
                      {isRtl ? roomsItems.find(i => i._id === selectedItem)?.title_ar : roomsItems.find(i => i._id === selectedItem)?.title_en}
                    </Typography>
                  </Stack>
                )}
                <Stack direction="row" justifyContent="space-between" alignItems="center" pt={1}>
                  <Typography fontWeight={900} variant="subtitle1">{t("bookRoom.total_price")}</Typography>
                  <Typography variant="h5" fontWeight={1000} color="primary">{totalPrice} EGP</Typography>
                </Stack>

                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  // 2. Disable button if a gap exists
                  disabled={totalHours === 0 || submitting || hasBookedSlotInRange}
                  onClick={async () => {
                    setSubmitting(true);

                    // 3. Construct the payload dynamically
                    const bookingData = {
                      room: room?._id,
                      client: user._id,
                      type: bookingKind,
                      startTime: selectedRange[0].start.toISOString(),
                      endTime: selectedRange[selectedRange.length - 1].start.toISOString(),
                    };

                    // Only add psService if it's selected and not "none"
                    if (selectedItem && selectedItem !== "none") {
                      bookingData.psService = selectedItem;
                    }

                    try {
                      await dispatch(createBooking({ data: bookingData })).unwrap();
                      navigate("/MyBookings");
                    } catch (e) {
                      console.error(e);
                      // Optional: Add a toast/alert here if the booking fails due to a gap
                    } finally {
                      setSubmitting(false);
                    }
                  }}
                  sx={{
                    py: 2, mt: 2, borderRadius: 2, fontWeight: 900,
                    background: hasBookedSlotInRange ? 'grey' : theme.palette.primary.gradient
                  }}
                >
                  {submitting ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : hasBookedSlotInRange ? (
                    isRtl ? "الفترة المختارة تحتوي على حجوزات" : "Range contains booked slots"
                  ) : (
                    t("bookRoom.confirm_booking")
                  )}
                </Button>
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* Adaptive UI */}
      {!isMobile ? (
        <Dialog open={openItemsMenu} onClose={() => setOpenItemsMenu(false)} fullWidth maxWidth="xs">
          <DialogTitle fontWeight={900} sx={{ textAlign: 'center' }}>{isRtl ? "تجهيز الغرفة" : "Room Setup"}</DialogTitle>
          <DialogContent><RenderOptions /></DialogContent>
        </Dialog>
      ) : (
        <SwipeableDrawer
          anchor="bottom" open={openItemsMenu}
          onClose={() => setOpenItemsMenu(false)} onOpen={() => setOpenItemsMenu(true)}
          PaperProps={{ sx: { borderTopLeftRadius: 24, borderTopRightRadius: 24 } }}
        >
          <Box sx={{ width: 40, height: 4, bgcolor: 'divider', borderRadius: 2, mx: 'auto', mt: 1.5, mb: 1 }} />
          <RenderOptions />
        </SwipeableDrawer>
      )}
    </Box>
  );
}